import { css } from "@emotion/css";
import { InlineMessage, RadioButtonNew, RadioGroupNew } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActionTemplateSearchResource, ProcessTemplateVersionSummary } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { TemplateBanner } from "~/areas/platformHub/TemplateBanner";
import { UpdateBehaviour, UsageConstraint } from "~/areas/projects/components/Templates/TemplateVersioningSection";
interface VersionSelectionStepProps {
    actionTemplate: ActionTemplateSearchResource;
    versionSummary: ProcessTemplateVersionSummary;
    selectedUsageConstraint: UsageConstraint;
    onUsageConstraintChanged: (value: UsageConstraint) => void;
}
export function VersionSelectionContent({ actionTemplate, versionSummary, selectedUsageConstraint, onUsageConstraintChanged }: VersionSelectionStepProps) {
    return (<div className={styles.stepContainer}>
            <TemplateBanner iconLink={actionTemplate.Links.Logo} heading={actionTemplate.Name} subHeading={getVersionInfo(versionSummary)}/>
            <div className={styles.selectionContainer}>
                <div className={styles.selectionHeading}>Select preferred version type</div>

                <RadioGroupNew label={"Version type selection"} value={selectedUsageConstraint} onChange={onUsageConstraintChanged} hideLabel={true}>
                    <RadioButtonNew value={UsageConstraint.Stable} label={"Stable"} name={"stable-button"}/>
                    <div className={styles.optionDescription}>Intended for production use. Recommended for most users.</div>
                    <RadioButtonNew value={UsageConstraint.PreRelease} label={"Pre-release"} name={"prerelease-button"}/>
                    <div className={styles.optionDescription}>Current version: {versionSummary.LatestPreReleaseVersion}. Pre-releases are intended for testing purposes, not regular deployments. Proceed with caution.</div>
                </RadioGroupNew>
            </div>
        </div>);
}
interface UpdateTypeSelectionStepProps {
    actionTemplate: ActionTemplateSearchResource;
    versionSummary: ProcessTemplateVersionSummary;
    selectedUpdateBehaviour: UpdateBehaviour;
    onUpdateBehaviourChanged: (value: UpdateBehaviour) => void;
}
export function UpdateBehaviourContent({ actionTemplate, versionSummary, selectedUpdateBehaviour, onUpdateBehaviourChanged }: UpdateTypeSelectionStepProps) {
    return (<div className={styles.stepContainer}>
            <TemplateBanner iconLink={actionTemplate.Links.Logo} heading={actionTemplate.Name} subHeading={getVersionInfo(versionSummary)}/>
            <div className={styles.selectionContainer}>
                <div className={styles.selectionHeading}>Select how you'd like the template to update</div>

                <RadioGroupNew label={"Update behavior selection"} value={selectedUpdateBehaviour} onChange={onUpdateBehaviourChanged} hideLabel={true}>
                    <RadioButtonNew label="Accept minor changes" value={UpdateBehaviour.Minor} name={"minor-button"}/>
                    <div className={styles.optionDescription}>Automatically update the template whenever a patch or minor version is published. Major versions will require a manual update.</div>
                    <RadioButtonNew label="Accept patches" value={UpdateBehaviour.Patch} name={"patch-button"}/>
                    <div className={styles.optionDescription}>Automatically update the template whenever a patch is published. Major and minor versions will require a manual update.</div>
                </RadioGroupNew>

                <InlineMessage type={"information"} description={"Major versions will always require a manual update."}/>
            </div>
        </div>);
}
function getVersionInfo(versionSummary: ProcessTemplateVersionSummary) {
    return (<>
            {versionSummary.LatestStableVersion && versionSummary.LatestPreReleaseVersion ? (<>
                    v{versionSummary.LatestStableVersion}, v{versionSummary.LatestPreReleaseVersion}
                </>) : versionSummary.LatestStableVersion ? (<>v{versionSummary.LatestStableVersion}</>) : versionSummary.LatestPreReleaseVersion ? (<>v{versionSummary.LatestPreReleaseVersion}</>) : null}
        </>);
}
const styles = {
    stepContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[32],
    }),
    selectionContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[16],
        marginBottom: space[32],
    }),
    selectionHeading: css({
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
        margin: 0,
    }),
    optionDescription: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.secondary,
        marginLeft: "1.75rem",
        marginBottom: space[16],
        lineHeight: 1.4,
    }),
};
